import { FC, FocusEvent, KeyboardEvent, useCallback } from 'react';
import { InputAttributes, NumberFormatBase, NumericFormatProps, useNumericFormat } from 'react-number-format';

type MyNumericFormatProps = Omit<NumericFormatProps<InputAttributes>, 'onBlur' | 'onKeyDown'> & {
  onBlur?: (value?: number) => void;
  onKeyDown: (e: KeyboardEvent<HTMLInputElement>, value?: number) => void;
  applyBoundaries: (value: number | undefined) => number | undefined;
};

export const MyNumericFormat: FC<MyNumericFormatProps> = ({ onBlur, onKeyDown, applyBoundaries, ...props }) => {
  const { removeFormatting, ...defaultProps } = useNumericFormat(props);

  const parseInputValue = useCallback(
    (text: string | undefined) => {
      if (text === undefined) {
        return undefined;
      }
      const unformatted = removeFormatting !== undefined ? removeFormatting(text) : text;
      const floatValue = parseFloat(unformatted);
      if (isNaN(floatValue)) {
        return undefined;
      }

      return applyBoundaries(floatValue);
    },
    [applyBoundaries, removeFormatting],
  );

  const handleBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      const rawValue = e.target.value;
      const value = parseInputValue(rawValue);
      if (onBlur) {
        onBlur(value);
      }
    },
    [onBlur, parseInputValue],
  );

  const handleOnKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      const rawValue = (e.target as HTMLInputElement).value;
      const value = parseInputValue(rawValue);
      onKeyDown(e, value);
    },
    [onKeyDown, parseInputValue],
  );

  return (
    <NumberFormatBase
      {...defaultProps}
      removeFormatting={removeFormatting}
      onBlur={handleBlur}
      onKeyDown={handleOnKeyDown}
    />
  );
};
